import { useEffect, useMemo, useState } from "react";
import api, { Recording, Room } from "../../../hooks/api-new";
import classNames from "./Meetings.module.css";
import { useAxios } from "../../../hooks/axios";
import Loader from "../../common/Loader";
import { getLanguageName } from "../../../types";
import {
  IoEllipsisHorizontal,
  IoPeopleOutline,
  IoVideocamOutline,
} from "react-icons/io5";
import { Link } from "react-router-dom";
import Button from "../../common/Button";
import EventDate from "../event/EventDate";
import Empty from "../../common/Empty";
import Search from "../../common/Search";
import Popover from "../../common/Popover";
import ConfirmActionDialog from "../../common/dialogs/ConfirmActionDialog";
import { toast } from "react-toastify";

function RoomOverflowPopover(props: {
  room: Room;
  setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
}) {
  const [open, setOpen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const axios = useAxios();
  const deleteRoom = api.useDeleteRoom(axios);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          color="gray"
          borderColor="transparent"
          backgroundColor="transparent"
        >
          <IoEllipsisHorizontal />
        </Button>
      }
      content={
        <div className={classNames.CardOverflowPopover}>
          <ConfirmActionDialog
            title={"Delete room"}
            trigger={
              <button onClick={() => setOpenConfirmDialog(true)}>Delete</button>
            }
            content={
              <h3>Are you sure you want to delete {props.room.name}?</h3>
            }
            action="Yes"
            onConfirm={async () => {
              try {
                await deleteRoom({ id: props.room.id });
                props.setRooms((prevState) =>
                  prevState.filter((room) => room.id !== props.room.id)
                );
                setOpenConfirmDialog(false);
                toast(
                  <div className="Toast SuccessToast">
                    <div>{"Successfully deleted room"}</div>
                  </div>,
                  { type: "success" }
                );
              } catch (error) {
                toast(
                  <div className="Toast ErrorToast">
                    {
                      "Something went wrong, please try again or contact support"
                    }
                  </div>,
                  {
                    type: "error",
                  }
                );
                throw error;
              }
            }}
            open={openConfirmDialog}
            onOpenChange={setOpenConfirmDialog}
          />
        </div>
      }
      align="end"
      side="bottom"
      contentClassName={classNames.CardOverflowPopoverContent}
    />
  );
}

function RoomCard(props: {
  room: Room;
  setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
}) {
  // TODO: Use better way to get short name
  const languages = useMemo(() => {
    return props.room.languages
      .map((l) => getLanguageName(l)?.split(" (")[0])
      .join(", ");
  }, [props.room.languages]);

  const roomType = useMemo(() => {
    switch (props.room.room_type) {
      case "meeting":
      case "teams":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Virtual
          </div>
        );
      case "event":
        return (
          <div className={classNames.Badge}>
            <IoPeopleOutline />
            In-person
          </div>
        );
    }
  }, [props.room.room_type]);

  const platform = useMemo(() => {
    switch (props.room.room_type) {
      case "meeting":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Langfinity
          </div>
        );
      case "event":
        return <div className={classNames.Badge}>Langfinity</div>;
      case "teams":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Microsoft Teams
          </div>
        );
    }
  }, [props.room.room_type]);

  return (
    <div className={classNames.RoomCard}>
      <div>
        <div className={classNames.RoomCardHeader}>
          <div>
            {roomType}
            {platform}
          </div>
        </div>
        {props.room.name && (
          <div className={classNames.RoomCardTitle}>{props.room.name}</div>
        )}
        <div className={classNames.RoomCardDetails}>
          {props.room.start_time && props.room.end_time && (
            <div>
              <EventDate
                start={props.room.start_time.toString()}
                end={props.room.end_time.toString()}
                reoccurring={props.room.reoccurring}
              />
            </div>
          )}
          <div>{languages}</div>
        </div>
      </div>
      <div>
        <Link
          to={`/${props.room.room_type}/${props.room.id}${
            props.room.room_type === "event" ? "/details" : ""
          }`}
        >
          <Button>
            {props.room.room_type === "meeting" ? "Join" : "View"}
          </Button>
        </Link>
        <Link to={`/edit/${props.room.id}`}>
          <Button color="gray" backgroundColor="dark-gray" borderColor="gray">
            Edit
          </Button>
        </Link>
        <RoomOverflowPopover {...props} />
      </div>
    </div>
  );
}

function RecordingOverflowPopover(props: {
  recording: Recording;
  setRecordings: React.Dispatch<React.SetStateAction<Recording[]>>;
}) {
  const [open, setOpen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const axios = useAxios();
  const deleteRecording = api.useDeleteRecording(axios);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          color="gray"
          borderColor="transparent"
          backgroundColor="transparent"
        >
          <IoEllipsisHorizontal />
        </Button>
      }
      content={
        <div className={classNames.CardOverflowPopover}>
          <ConfirmActionDialog
            title={"Delete recording"}
            trigger={
              <button onClick={() => setOpenConfirmDialog(true)}>Delete</button>
            }
            content={
              <h3>Are you sure you want to delete {props.recording.name}?</h3>
            }
            action="Yes"
            onConfirm={async () => {
              try {
                await deleteRecording({ recording_id: props.recording.id });
                props.setRecordings((prevState) =>
                  prevState.filter(
                    (recording) => recording.id !== props.recording.id
                  )
                );
                setOpenConfirmDialog(false);
                toast(
                  <div className="Toast SuccessToast">
                    <div>{"Successfully deleted recording"}</div>
                  </div>,
                  { type: "success" }
                );
              } catch (error) {
                toast(
                  <div className="Toast ErrorToast">
                    {
                      "Something went wrong, please try again or contact support"
                    }
                  </div>,
                  {
                    type: "error",
                  }
                );
                throw error;
              }
            }}
            open={openConfirmDialog}
            onOpenChange={setOpenConfirmDialog}
          />
        </div>
      }
      align="end"
      side="bottom"
      contentClassName={classNames.CardOverflowPopoverContent}
    />
  );
}

function RecordingCard(props: {
  recording: Recording;
  setRecordings: React.Dispatch<React.SetStateAction<Recording[]>>;
}) {
  const languages = useMemo(() => {
    return props.recording.languages
      .map((l) => getLanguageName(l)?.split(" (")[0])
      .join(", ");
  }, [props.recording.languages]);

  const roomType = useMemo(() => {
    switch (props.recording.room_type) {
      case "meeting":
      case "teams":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Virtual
          </div>
        );
      case "event":
        return (
          <div className={classNames.Badge}>
            <IoPeopleOutline />
            In-person
          </div>
        );
    }
  }, [props.recording.room_type]);

  const platform = useMemo(() => {
    switch (props.recording.room_type) {
      case "meeting":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Langfinity
          </div>
        );
      case "event":
        return <div className={classNames.Badge}>Langfinity</div>;
      case "teams":
        return (
          <div className={classNames.Badge}>
            <IoVideocamOutline />
            Microsoft Teams
          </div>
        );
    }
  }, [props.recording.room_type]);

  return (
    <div className={classNames.RoomCard}>
      <div>
        <div className={classNames.RoomCardHeader}>
          <div>
            {roomType}
            {platform}
          </div>
        </div>
        {props.recording.name && (
          <div className={classNames.RoomCardTitle}>{props.recording.name}</div>
        )}
        <div className={classNames.RoomCardDetails}>
          {props.recording.created_at && props.recording.stopped_at && (
            <div>
              <EventDate
                start={props.recording.created_at}
                end={props.recording.stopped_at}
                reoccurring={false}
              />
            </div>
          )}
          <div>{languages}</div>
        </div>
      </div>
      <div>
        <Link to={`/recording/${props.recording.id}`}>
          <Button>View</Button>
        </Link>
        <RecordingOverflowPopover {...props} />
      </div>
    </div>
  );
}

export default function Meetings(props: { hidePast: boolean }) {
  const axios = useAxios();
  const getRoomList = api.useGetRoomList(axios);
  const getRecordingList = api.useGetRecordingList(axios);

  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);

  useEffect(() => {
    (async () => {
      let waitForSetRooms = false;
      setLoading(true);
      try {
        {
          const response = await getRoomList();
          waitForSetRooms = response.data.rooms.length > 0;
          setRooms(response.data.rooms);
        }
        if (!props.hidePast) {
          const response = await getRecordingList();
          setRecordings(
            response.data.recordingList.filter(
              (recording: Recording) => recording.stopped_at !== null
            )
          );
        }
      } finally {
        // Hack to make sure setRooms finishes so we don't briefly show empty state (since both sets are async)
        setTimeout(
          () => {
            setLoading(false);
          },
          waitForSetRooms ? 500 : 0
        );
      }
    })();
  }, [getRoomList, getRecordingList, props.hidePast]);

  const upcomingRooms = useMemo(() => {
    // 2 hour padding
    const now = new Date();
    now.setHours(now.getHours() - 2);

    const upcomingRooms = rooms
      .filter((room) => room.room_type === "event")
      .filter((room) => {
        return (
          new Date(room.start_time || room.created_at) > now || room.reoccurring
        );
      })
      .sort(
        (a, b) =>
          new Date(b.start_time || b.created_at).getTime() -
          new Date(a.start_time || a.created_at).getTime()
      );

    return upcomingRooms;
  }, [rooms]);

  const sortedRecordings = useMemo(() => {
    return recordings.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [recordings]);

  const [searchQuery, setSearchQuery] = useState("");

  const [filters, setFilters] = useState<Room["room_type"][]>([]);

  if (loading) {
    return <Loader />;
  }

  // Completely hide in home page when no upcoming
  if (props.hidePast && !upcomingRooms.length) {
    return null;
  }

  if (!upcomingRooms.length && !recordings.length) {
    return (
      <div className={classNames.Meetings}>
        <h1>Meetings & events</h1>
        <div className={classNames.Empty}>
          <Empty
            message={
              <>
                You currently have no multilingual meetings or events scheduled.{" "}
                <Link to="/">Click here to set one up now!</Link>
              </>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames.Meetings}>
      {!props.hidePast && <h1>Meetings & events</h1>}
      {!props.hidePast && (
        <div className={classNames.FiltersAndSearch}>
          <div className={classNames.Filters}>
            <Button
              backgroundColor={
                filters.includes("teams") ? "dark-gray" : "black"
              }
              borderColor="dark-gray"
              color={filters.includes("teams") ? "white" : "gray"}
              onClick={() =>
                setFilters((prev) => {
                  if (prev.includes("teams")) {
                    return prev.filter((f) => f !== "teams");
                  }
                  return [...prev, "teams"];
                })
              }
            >
              Teams
            </Button>
            <Button
              backgroundColor={
                filters.includes("event") ? "dark-gray" : "black"
              }
              borderColor="dark-gray"
              color={filters.includes("event") ? "white" : "gray"}
              onClick={() =>
                setFilters((prev) => {
                  if (prev.includes("event")) {
                    return prev.filter((f) => f !== "event");
                  }
                  return [...prev, "event"];
                })
              }
            >
              In-person
            </Button>
            <Button
              backgroundColor={
                filters.includes("meeting") ? "dark-gray" : "black"
              }
              borderColor="dark-gray"
              color={filters.includes("meeting") ? "white" : "gray"}
              onClick={() =>
                setFilters((prev) => {
                  if (prev.includes("meeting")) {
                    return prev.filter((f) => f !== "meeting");
                  }
                  return [...prev, "meeting"];
                })
              }
            >
              Virtual
            </Button>
          </div>
          <Search
            query={searchQuery}
            setQuery={setSearchQuery}
            placeholder="Search for a meeting or an event"
            className={classNames.Search}
          />
        </div>
      )}
      {!!upcomingRooms.length && (
        <div className={classNames.List}>
          <h2>Upcoming</h2>
          <>
            {upcomingRooms
              // Filters
              .filter((r) => !filters.length || filters.includes(r.room_type))
              // Search
              .filter(
                (r) =>
                  !searchQuery.length ||
                  (r.name &&
                    r.name.toLowerCase().includes(searchQuery.toLowerCase()))
              )
              .map((room) => (
                <div key={room.id}>
                  <RoomCard room={room} setRooms={setRooms} />
                </div>
              ))}
          </>
        </div>
      )}
      {!props.hidePast && !!sortedRecordings.length && (
        <div className={classNames.List}>
          <h2>Recordings</h2>
          {sortedRecordings
            // Filters
            .filter((r) => !filters.length || filters.includes(r.room_type))
            // Search
            .filter(
              (r) =>
                !searchQuery.length ||
                r.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((recording) => (
              <div key={recording.id}>
                <RecordingCard
                  recording={recording}
                  setRecordings={setRecordings}
                />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
